import { api } from "@/lib/api/api";
import { FpApiError } from "@/lib/api/errors";
import type { ParsedParam } from "@/lib/mcp/schema";
import { useInspectorStore } from "@/stores/inspectorStore";
import { useCallback } from "react";
import {
  createExecutionHistoryEntry,
  createToolExecutionRequest,
  extractMcpResult,
} from "../shared/utils/execution";
import { coerceAllParameters } from "../shared/utils/validation";

export interface UseToolExecutionOptions {
  sessionId: string;
  toolName: string | null;
  parsedParams: ParsedParam[];
}

export function useToolExecution({
  sessionId,
  toolName,
  parsedParams,
}: UseToolExecutionOptions) {
  const {
    getToolState,
    setToolExecuting,
    setToolResponse,
    setToolError,
    addToolExecution,
  } = useInspectorStore();

  const currentState = toolName ? getToolState(toolName) : null;
  const isLoading = currentState?.isExecuting || false;
  const result = currentState?.lastResponse || null;
  const error = currentState?.lastError || null;
  const parameters = currentState?.parameters || {};
  const paramErrors = currentState?.paramErrors || {};

  const executeTool = useCallback(async () => {
    if (!toolName || !sessionId) {
      return;
    }

    const startTime = Date.now();
    const executionId = `tool-${toolName}-${startTime}`;

    // Coerce parameters to correct types (do this outside try to access in catch)
    const { args, errors } = coerceAllParameters(parameters, parsedParams);

    try {
      // Validate parameters
      const hasErrors = Object.values(paramErrors).some((error) => error);
      if (hasErrors) {
        throw new Error(
          "Please fix parameter validation errors before executing",
        );
      }

      // Check for coercion errors
      const hasCoercionErrors = Object.values(errors).some((error) => error);
      if (hasCoercionErrors) {
        throw new Error("Parameter validation failed");
      }

      // Set loading state
      setToolExecuting(toolName, true);
      setToolError(toolName, null);
      setToolResponse(toolName, null);

      // Create execution request
      const request = createToolExecutionRequest(toolName, args);

      // Make API call
      const { payload } = await api.mcpRpc(sessionId, request);

      // Extract result
      const mcpResult = extractMcpResult(payload);

      // Update state
      setToolResponse(toolName, mcpResult);

      // Add to execution history
      const historyEntry = createExecutionHistoryEntry({
        id: executionId,
        startTime,
        parameters: args,
        result: mcpResult,
        status: "success",
      });
      addToolExecution(toolName, {
        ...historyEntry,
        parameters: args, // Ensure parameters is always defined
      });
    } catch (err) {
      const errorMessage =
        err instanceof FpApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "An unexpected error occurred";

      // Update error state
      setToolError(toolName, errorMessage);

      // Add to execution history
      const historyEntry = createExecutionHistoryEntry({
        id: executionId,
        startTime,
        parameters: args,
        error: errorMessage,
        status: "error",
      });
      addToolExecution(toolName, {
        ...historyEntry,
        parameters: args, // Ensure parameters is always defined
      });
    } finally {
      setToolExecuting(toolName, false);
    }
  }, [
    toolName,
    sessionId,
    parameters,
    paramErrors,
    parsedParams,
    setToolExecuting,
    setToolResponse,
    setToolError,
    addToolExecution,
  ]);

  const clearResults = useCallback(() => {
    if (!toolName) {
      return;
    }

    setToolResponse(toolName, null);
    setToolError(toolName, null);
  }, [toolName, setToolResponse, setToolError]);

  return {
    isLoading,
    result,
    error,
    executeTool,
    clearResults,
    canExecute:
      !!toolName && Object.keys(paramErrors).every((key) => !paramErrors[key]),
  };
}
